import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import {
  PlatformView, FormValues, FileState, ExecuteResult,
  ActionConfig, ActionConditions, BackendExecution,
} from '@/types/platform';
import { platformService } from '@/services/platformService';
import { extractErrorMessage } from '@/utils/fetchErrors';
import SectionRenderer from './SectionRenderer.tsx';
import PostProcessing from './PostProcessing.tsx';
import CreditBalance from './CreditBalance.tsx';
import ActionButton from './ActionButton';
import InlinePanel from './InlinePanel.tsx';
import TopActionPanel from './TopActionPanel.tsx';
import './DynamicPlatformView.css';

interface DynamicPlatformViewProps {
  platformView: PlatformView;
}

// ─── Runtime state that conditions are evaluated against ──────────────────────
interface ViewState {
  hasResult: boolean;
  hasSelectedFiles: boolean;
  insertedToExcel: boolean;
  hasSpreadsheetData: boolean;
  formValid: boolean;
  selectedFileExtensions: Set<string>;
  selectedDocumentType?: number | string;
}

// ─── Condition evaluator ──────────────────────────────────────────────────────
function evalConditions(cond: ActionConditions | undefined, state: ViewState): boolean {
  if (!cond) return true;

  // If anyOf is present, use OR logic: at least one sub-condition must pass
  if (cond.anyOf && cond.anyOf.length > 0) {
    return cond.anyOf.some((sub) => evalConditions(sub, state));
  }

  if (cond.hasResult !== undefined && cond.hasResult !== state.hasResult) return false;
  if (cond.hasSelectedFiles !== undefined && cond.hasSelectedFiles !== state.hasSelectedFiles) return false;
  if (cond.insertedToExcel !== undefined && cond.insertedToExcel !== state.insertedToExcel) return false;
  if (cond.hasSpreadsheetData !== undefined && cond.hasSpreadsheetData !== state.hasSpreadsheetData) return false;
  if (cond.formValid !== undefined && cond.formValid !== state.formValid) return false;
  if (cond.selectedFileExtensionIn) {
    const hasMatch = cond.selectedFileExtensionIn.some(ext => state.selectedFileExtensions.has(ext));
    if (!hasMatch) return false;
  }
  if (cond.selectedDocumentType !== undefined) {
    if (String(cond.selectedDocumentType) !== String(state.selectedDocumentType)) return false;
  }
  return true;
}

const DynamicPlatformView: React.FC<DynamicPlatformViewProps> = ({ platformView }) => {
  const { t, i18n } = useTranslation();
  const { viewConfig } = platformView;

  // ─── Form state ─────────────────────────────────────────────────────────────
  const [formValues, setFormValues] = useState<FormValues>(() => {
    const defaults: FormValues = {};
    viewConfig.sections.forEach(section => {
      section.components.forEach(component => {
        if (component.defaultValue !== undefined) {
          defaults[component.name] = component.defaultValue;
        }
      });
    });
    return defaults;
  });

  const [i18nReady, setI18nReady] = useState(() => i18n.hasResourceBundle('bg', viewConfig.i18nNamespace));
  const [files, setFiles] = useState<FileState>({});
  const [isSubmitting, setIsSubmitting] = useState<string | null>(null);
  const [executeError, setExecuteError] = useState<string | null>(null);
  const [result, setResult] = useState<ExecuteResult | null>(null);
  const [tableRows, setTableRows] = useState<string[][]>([]);
  const [insertedToExcel, setInsertedToExcel] = useState(false);
  const [hasEverInsertedToExcel, setHasEverInsertedToExcel] = useState(false);
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [openResultPanelId, setOpenResultPanelId] = useState<string | null>(null);
  const [openBottomPanelId, setOpenBottomPanelId] = useState<string | null>(null);

  const creditWidget = viewConfig.widgets?.creditBalance;

  // ─── i18n + credit balance bootstrap ────────────────────────────────────────
  useEffect(() => {
    const namespace = viewConfig.i18nNamespace;
    if (!i18n.hasResourceBundle('bg', namespace)) {
      import('@/i18n/locales/bg/platform.json').then((translations) => {
        const parts = namespace.split('.');
        let data: any = translations.default;
        for (const part of parts) { data = data?.[part]; }
        if (data) i18n.addResourceBundle('bg', namespace, data);
        setI18nReady(true);
      });
    }
    refreshCreditBalance();
  }, [viewConfig.i18nNamespace]);

  const refreshCreditBalance = async () => {
    if (!creditWidget?.visible) return;
    try {
      const balance = await platformService.getCreditBalance();
      setCreditBalance(balance);
    } catch { /* non-critical */ }
  };

  // ─── Helpers ─────────────────────────────────────────────────────────────────
  const getTranslation = (key: string) =>
    t(key, { ns: viewConfig.i18nNamespace, defaultValue: key });

  const handleValueChange = (name: string, value: any) => {
    setFormValues(prev => {
      const updated = { ...prev, [name]: value };

      // Apply dynamicDefaults if configured
      // Skip overwriting the field the user is currently editing directly,
      // unless that field is a trigger field referenced in a rule's "when" condition.
      const dynamicDefaults = (viewConfig as any).dynamicDefaults;
      if (dynamicDefaults) {
        for (const group of Object.values(dynamicDefaults) as any[]) {
          if (!Array.isArray(group)) continue;
          for (const rule of group) {
            if (!rule.when || !rule.values) continue;
            const allMatch = Object.entries(rule.when).every(
              ([field, expected]) => String(updated[field] ?? '') === String(expected)
            );
            if (allMatch) {
              // If the edited field is a trigger (part of "when"), apply all values.
              // If the edited field is a target (not in "when"), skip it to allow user typing.
              const isTriggerField = name in rule.when;
              if (isTriggerField) {
                Object.assign(updated, rule.values);
              } else {
                for (const [k, v] of Object.entries(rule.values)) {
                  if (k !== name) {
                    (updated as any)[k] = v;
                  }
                }
              }
            }
          }
        }
      }

      // Apply config-driven events (e.g. swapValues when documentType changes)
      if (viewConfig.events) {
        for (const fieldEvent of viewConfig.events) {
          if (fieldEvent.event !== 'change' || fieldEvent.field !== name) continue;
          for (const action of fieldEvent.actions) {
            // Check optional 'when' condition on the action
            if (action.when && String(action.when.equals) !== String(value)) continue;

            if (action.type === 'swapValues') {
              const [fieldA, fieldB] = action.fields;
              const valA = updated[fieldA];
              const valB = updated[fieldB];
              updated[fieldA] = valB;
              updated[fieldB] = valA;
            }
          }
        }
      }

      return updated;
    });
  };

  const handleFileChange = (name: string, fileList: File[]) =>
    setFiles(prev => ({ ...prev, [name]: fileList }));

  const isComponentVisible = (component: any, values: FormValues): boolean => {
  const condition = component.visibleWhen;
  if (!condition) return true;

  if (condition.field && condition.equals !== undefined) {
    return String(values[condition.field] ?? '') === String(condition.equals);
  }

  if (condition.field && condition.notEquals !== undefined) {
    return String(values[condition.field] ?? '') !== String(condition.notEquals);
  }

  if (condition.field && condition.in && Array.isArray(condition.in)) {
    const currentValue = String(values[condition.field] ?? '');
    return condition.in.some((v: any) => String(v) === currentValue);
  }

  return true;
};

  const handleCellChange = useCallback((rowIndex: number, cellIndex: number, value: string) => {
    setTableRows(prev => {
      const updated = prev.map(r => [...r]);
      updated[rowIndex][cellIndex] = value;
      return updated;
    });
  }, []);

  // ─── Derived view state (used by condition evaluator) ────────────────────────
  const allUploadedFiles = Object.values(files).flat();
  const hasSelectedFiles = allUploadedFiles.length > 0;
  const selectedFileExtensions = new Set(
    allUploadedFiles.map(f => '.' + f.name.split('.').pop()?.toLowerCase())
  );

  const viewState: ViewState = {
    hasResult: result !== null,
    hasSelectedFiles,
    insertedToExcel,
    hasSpreadsheetData: hasEverInsertedToExcel,
    formValid: true, // all required fields have defaults; extend if needed
    selectedFileExtensions,
    selectedDocumentType: formValues['documentType'],
  };

  // ─── Resolve form payload (applies payloadKey remapping + custom value labels) ─
  const resolveFormPayload = (values: FormValues): FormValues => {
    const resolved: FormValues = {};
    viewConfig.sections.forEach(section => {
    section.components
      .filter(component => isComponentVisible(component, values))
      .forEach(component => {
        const targetKey = component.payloadKey ?? component.name;
        resolved[targetKey] = values[component.name];

        // Replace custom-value selects with their translated label
        if (
          component.type === 'select' &&
          component.allowCustomValue &&
          component.name !== 'documentType' &&
          component.options
        ) {
          const currentValue = values[component.name];
          const matched = component.options.find(o => String(o.value) === String(currentValue));
          if (matched) resolved[targetKey] = getTranslation(matched.labelKey);
        }
      });
  });
    return resolved;
  };

  // ─── Handle result from a successful backend execution ───────────────────────
  const applyExecuteResult = (raw: any) => {
    if (raw === null || raw === undefined) return; // some actions (e.g. product list import) return null data
    const executeResult: ExecuteResult = typeof raw === 'string' ? JSON.parse(raw) : raw;
    setResult(executeResult);
    setTableRows(executeResult.data ?? []);
    setInsertedToExcel(false);
    refreshCreditBalance();
  };

  // ─── Read current spreadsheet data from Excel ────────────────────────────────
  const readSpreadsheetData = async (): Promise<string[][] | null> => {
    if (!window.Excel) return null;
    try {
      let data: string[][] = [];
      await window.Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getActiveWorksheet();
        const usedRange = sheet.getUsedRangeOrNullObject();
        usedRange.load(['isNullObject', 'values']);
        await context.sync();
        if (!usedRange.isNullObject) {
          data = (usedRange.values as any[][]).map(row =>
            row.map(cell => String(cell ?? ''))
          );
        }
      });
      return data.length > 0 ? data : null;
    } catch {
      return null;
    }
  };

  // ─── Execute a config-driven action ──────────────────────────────────────────
  const executeAction = async (
    action: ActionConfig,
    panelFormValues?: FormValues,
    panelFiles?: File[]
  ) => {
    if (!action.execution) return;
    setIsSubmitting(action.id);
    setExecuteError(null);

    try {
      if (action.execution.handler === 'frontend') {
        switch (action.execution.action) {
          case 'INSERT_TO_EXCEL':
            await handleInsertToExcel();
            break;
          case 'SAVE_RESULT_AS_CSV':
            await handleSaveCsv();
            break;
          case 'SAVE_RESULT_AS_XLSX':
            await handleSaveXlsx();
            break;
          case 'RESET_ANALYSIS':
            handleNewAnalysis();
            break;
        }
        return;
      }

      // Backend execution
      const exec = action.execution as BackendExecution;

      // If this action needs table data, prefer live spreadsheet over in-memory state
      let resolvedTableRows = tableRows;
      if (exec.include?.tableData) {
        const liveData = await readSpreadsheetData();
        if (liveData) resolvedTableRows = liveData;
      }

      const response = await platformService.executeAction(exec, {
        platformId: platformView.platformId,
        formValues: resolveFormPayload(formValues),
        panelFormValues,
        files,
        panelFiles,
        tableRows: resolvedTableRows,
      });

      if (exec.responseType === 'file') {
        if (!response.success) {
          setExecuteError(response.errorMessage ?? 'Export failed.');
          return;
        }
        // data is { files: [{ fileName, base64Content, contentType }, ...] }
        const exportFiles: { fileName: string; base64Content: string; contentType: string }[] =
          response.data?.files ?? [];
        for (const file of exportFiles) {
          const byteChars = atob(file.base64Content);
          const byteArray = new Uint8Array(byteChars.length);
          for (let i = 0; i < byteChars.length; i++) {
            byteArray[i] = byteChars.charCodeAt(i);
          }
          const blob = new Blob([byteArray], { type: file.contentType });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = file.fileName;
          a.click();
          URL.revokeObjectURL(url);
        }
        return;
      }

      if (exec.responseType === 'inline-file') {
        if (!response.success) {
          setExecuteError(response.errorMessage ?? 'Export failed.');
          return;
        }
        // data is { fileName, contentType, content, base64Content?, charset? }
        const fileData = response.data?.data ?? response.data;
        if (fileData?.fileName) {
          let blob: Blob;
          
          // Prefer base64Content when present (for specific encodings like windows-1251)
          if (fileData.base64Content != null && fileData.base64Content !== '') {
            const byteChars = atob(fileData.base64Content);
            const byteArray = new Uint8Array(byteChars.length);
            for (let i = 0; i < byteChars.length; i++) {
              byteArray[i] = byteChars.charCodeAt(i);
            }
            blob = new Blob([byteArray], {
              type: fileData.contentType || 'application/octet-stream',
            });
          } else if (fileData.content != null) {
            // Fallback to plain content (for UTF-8 text files)
            blob = new Blob([fileData.content], {
              type: fileData.contentType || 'application/octet-stream',
            });
          } else {
            setExecuteError('File data is missing content.');
            return;
          }

          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileData.fileName;
          a.click();
          URL.revokeObjectURL(url);
        }
        return;
      }

      // Detect inline-file response shape even when responseType is 'json' (DB not yet updated)
      if (response.success) {
        const maybeFile = response.data?.data;
        if (maybeFile?.fileName && maybeFile?.contentType) {
          let blob: Blob;
          
          // Prefer base64Content when present (for specific encodings like windows-1251)
          if (maybeFile.base64Content != null && maybeFile.base64Content !== '') {
            const byteChars = atob(maybeFile.base64Content);
            const byteArray = new Uint8Array(byteChars.length);
            for (let i = 0; i < byteChars.length; i++) {
              byteArray[i] = byteChars.charCodeAt(i);
            }
            blob = new Blob([byteArray], { type: maybeFile.contentType });
          } else if (maybeFile.content != null) {
            // Fallback to plain content (for UTF-8 text files)
            blob = new Blob([maybeFile.content], { type: maybeFile.contentType });
          } else {
            // No valid content to download
            applyExecuteResult(response.data);
            return;
          }

          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = maybeFile.fileName;
          a.click();
          URL.revokeObjectURL(url);
          return;
        }
        applyExecuteResult(response.data);
      } else {
        const errorMsg = response.errorMessage ?? 'An unexpected error occurred.';
        setExecuteError(errorMsg);
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error('Action execution error:', error);
      setExecuteError(extractErrorMessage(error));
      throw error;
    } finally {
      setIsSubmitting(null);
    }
  };

  // ─── Frontend action: Insert to Excel ────────────────────────────────────────
  const handleInsertToExcel = async () => {
    if (!window.Excel) return;

    const headerRow = tableRows[0] ?? [];
    const dataRows = tableRows.slice(1);

    // Build dropdown lists from any select component that has options
    const getColumnOptions = (componentName: string): string[] => {
      for (const section of viewConfig.sections) {
        const comp = section.components.find(c => c.name === componentName);
        if (comp?.options) {
          const translated = comp.options.map(o => getTranslation(o.labelKey));
          const current = formValues[componentName];
          if (current && !comp.options.some(o => String(o.value) === String(current))) {
            translated.push(String(current));
          }
          return translated;
        }
      }
      return [];
    };

    // PlusMinus-specific dropdown columns — only applied when those components exist
    const objectTypeOptions = getColumnOptions('objectType');
    const entityTypeOptions = getColumnOptions('entityType');

    await window.Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();

      const usedRange = sheet.getUsedRangeOrNullObject();
      usedRange.load(['isNullObject', 'rowCount']);
      await context.sync();

      let startRow = 0;
      let isEmpty = true;

      if (!usedRange.isNullObject && usedRange.rowCount > 0) {
        isEmpty = false;
        startRow = usedRange.rowCount;
      }

      const rowsToInsert = isEmpty ? [headerRow, ...dataRows] : dataRows;
      if (rowsToInsert.length === 0) return;

      // Normalize to rectangular array
      const colCount = Math.max(...rowsToInsert.map(r => r.length));
      const normalized = rowsToInsert.map(r =>
        r.length === colCount ? r : [...r, ...Array(colCount - r.length).fill('')]
      );

      const range = sheet.getRangeByIndexes(startRow, 0, normalized.length, colCount);
      // Important: prevent Excel from converting codes like 00101 to 101
      range.numberFormat = normalized.map(row => row.map(() => '@'));
      range.values = normalized;

      if (isEmpty) {
        const headerRange = sheet.getRangeByIndexes(0, 0, 1, colCount);
        headerRange.format.fill.color = '#4472C4';
        headerRange.format.font.color = '#FFFFFF';
        headerRange.format.font.bold = true;
      }

      const fullRange = sheet.getRangeByIndexes(0, 0, startRow + normalized.length, colCount);
      fullRange.format.autofitColumns();

      // Apply dropdowns only when the relevant components exist in this platform's config
      const dataStartRow = isEmpty ? 1 : startRow;
      const dataRowCount = isEmpty ? dataRows.length : normalized.length;

      if (dataRowCount > 0) {
        if (objectTypeOptions.length > 0) {
          const r = sheet.getRangeByIndexes(dataStartRow, 6, dataRowCount, 1);
          r.dataValidation.rule = { list: { inCellDropDown: true, source: objectTypeOptions.join(',') } };
          r.dataValidation.prompt = { showPrompt: false, message: '', title: '' };
          r.dataValidation.errorAlert = { showAlert: false, message: '', title: '', style: Excel.DataValidationAlertStyle.stop };
        }
        if (entityTypeOptions.length > 0) {
          const r = sheet.getRangeByIndexes(dataStartRow, 7, dataRowCount, 1);
          r.dataValidation.rule = { list: { inCellDropDown: true, source: entityTypeOptions.join(',') } };
          r.dataValidation.prompt = { showPrompt: false, message: '', title: '' };
          r.dataValidation.errorAlert = { showAlert: false, message: '', title: '', style: Excel.DataValidationAlertStyle.stop };
        }
      }

      await context.sync();
    });

    setInsertedToExcel(true);
    setHasEverInsertedToExcel(true);
  };

  // ─── Frontend action: Save as CSV ─────────────────────────────────────────────
const handleSaveCsv = async () => {
  if (!window.Excel) {
    throw new Error('Excel API is not available.');
  }

  let allRows: string[][] | null = null;

  // Read the spreadsheet data from Excel.
  // Retry once in case Office.js has a transient/stale context.
  for (let attempt = 1; attempt <= 2; attempt++) {
    try {
      allRows = await window.Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getActiveWorksheet();
        const usedRange = sheet.getUsedRange();

        usedRange.load('values');
        await context.sync();

        return (usedRange.values as any[][]).map(row =>
          row.map(cell => String(cell ?? ''))
        );
      });

      break;
    } catch (error) {
      console.warn(`CSV Excel read failed (attempt ${attempt})`, error);

      if (attempt === 2) {
        throw error;
      }

      // Give Office.js a short moment before retrying.
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  }

  if (!allRows || allRows.length === 0) {
    throw new Error('No spreadsheet data available.');
  }

  // Everything below this point runs outside Excel.run().
  const headerRow = allRows[0] ?? [];
  const dataRows = allRows.slice(1);
  const colCount = headerRow.length;

  const now = new Date();
  const datePart = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');

  const BOM = '\uFEFF';

  const downloadFile = (
    content: string,
    filename: string,
    mime = 'text/csv;charset=utf-8;'
  ) => {
    const blob = new Blob([BOM + content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');

    a.href = url;
    a.download = filename;

    document.body.appendChild(a);
    a.click();
    a.remove();

    // Give the browser/WebView time to start the download
    // before releasing the object URL.
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 1000);
  };

  // Primary DOCUMENTS file — first 44 columns, data rows only, no header
  const documentsCsv = dataRows
    .map(row => row.slice(0, 44).join(';'))
    .join('\r\n');

  downloadFile(
    documentsCsv,
    `DOCUMENTS-${datePart}.csv`
  );

  // Extended columns (49+): split into CLIENTS / SUPPLIERS
  if (colCount >= 49) {
    const extendedHeader = headerRow.slice(44).join(';');

    const clientRows = dataRows.filter(row =>
      row[44]?.startsWith('КЛИЕНТИ')
    );

    const supplierRows = dataRows.filter(row =>
      row[44]?.startsWith('ДОСТАВЧИЦИ')
    );

    if (clientRows.length > 0) {
      downloadFile(
        [extendedHeader, ...clientRows.map(r => r.slice(44).join(';'))].join('\r\n'),
        `CLIENTS-${datePart}.csv`
      );
    }

    if (supplierRows.length > 0) {
      downloadFile(
        [extendedHeader, ...supplierRows.map(r => r.slice(44).join(';'))].join('\r\n'),
        `SUPPLIERS-${datePart}.csv`
      );
    }
  }
};

  // ─── Frontend action: Save as XLSX ────────────────────────────────────────────
  const handleSaveXlsx = async () => {
    if (!window.Excel) return;

    await window.Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      const usedRange = sheet.getUsedRangeOrNullObject();
      usedRange.load(['isNullObject', 'values']);
      await context.sync();

      if (usedRange.isNullObject) return;

      const allRows: any[][] = usedRange.values as any[][];
      if (allRows.length === 0) return;

      // Create workbook from spreadsheet data
      const ws = XLSX.utils.aoa_to_sheet(allRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

      // Generate file with current date as name
      const now = new Date();
      const datePart = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, '0'),
        String(now.getDate()).padStart(2, '0'),
      ].join('-');

      const xlsxData = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([xlsxData], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${datePart}.xlsx`;
      a.click();
      setTimeout(() => {
          URL.revokeObjectURL(url);
      }, 1000);
    });
  };

  // ─── Frontend action: Reset ───────────────────────────────────────────────────
  const handleNewAnalysis = () => {
    setResult(null);
    setTableRows([]);
    setFiles({});
    setInsertedToExcel(false);
    setExecuteError(null);
  };

  // ─── Legacy v1/v2: derive submit buttons from processingModes on fileUpload ───
  const legacySubmitButtons = (() => {
    if (viewConfig.fileActions) return []; // v3 — not used
    const modes: Array<{ id: string; buttonLabelKey: string; strategy: string; visibleForExtensions?: string[] }> = [];
    for (const section of viewConfig.sections) {
      for (const comp of section.components) {
        if (comp.type === 'fileUpload' && comp.processingModes) {
          return comp.processingModes;
        }
      }
    }
    if (hasSelectedFiles) {
      // Fallback: single AI button
      let defaultMode = '';
      for (const section of viewConfig.sections) {
        for (const comp of section.components) {
          if (comp.type === 'fileUpload' && comp.defaultProcessingMode) {
            defaultMode = comp.defaultProcessingMode;
          }
        }
      }
      return [{ id: defaultMode || '__default__', buttonLabelKey: 'form.submit', strategy: '' }];
    }
    return modes;
  })();

  const legacyVisibleSubmitButtons = legacySubmitButtons.filter(mode => {
    if (!hasSelectedFiles) return false;
    if (!mode.visibleForExtensions || mode.visibleForExtensions.length === 0) return true;
    return mode.visibleForExtensions.some(ext => selectedFileExtensions.has(ext));
  });

  // Legacy submit handler
  const handleLegacySubmit = async (mode: { id: string; strategy: string }) => {
    setIsSubmitting(mode.id || '__default__');
    setExecuteError(null);
    try {
      const response = await platformService.executePlatform(
        platformView.platformId,
        resolveFormPayload(formValues),
        files,
        mode.strategy || 'AI'
      );
      if (response.success) {
        applyExecuteResult(response.data);
      } else {
        setExecuteError(response.errorMessage ?? 'An unexpected error occurred.');
      }
    } catch (error) {
      setExecuteError(extractErrorMessage(error));
    } finally {
      setIsSubmitting(null);
    }
  };

  // Legacy post-processing submit
  const handlePostProcessingSubmit = async (
    _actionId: string,
    strategy: string,
    processingMode: { documentTypeOverride?: number | null },
    values: Record<string, any>
  ) => {
    const documentType = processingMode.documentTypeOverride ?? formValues['documentType'];
    let spreadsheetData: string[][] = [];
    if (window.Excel) {
      await window.Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getActiveWorksheet();
        const usedRange = sheet.getUsedRange();
        usedRange.load('values');
        await context.sync();
        spreadsheetData = (usedRange.values as string[][]).map(row =>
          row.map(cell => String(cell ?? ''))
        );
      });
    }
    const response = await platformService.executePostProcessing(
      platformView.platformId,
      strategy,
      { ...values, documentType },
      spreadsheetData
    );
    if (!response.success) throw new Error(response.errorMessage ?? 'An unexpected error occurred.');
    applyExecuteResult(response.data);
  };

  // ─── Render ───────────────────────────────────────────────────────────────────
  const headerRow = tableRows[0] ?? [];
  const dataRows = tableRows.slice(1);
  const rowCount = result?.rowsProcessed ?? dataRows.length;

  if (!i18nReady) {
    return <div className="platform-loader"><p>Зареждане...</p></div>;
  }

  const isV3 = !!viewConfig.fileActions || !!viewConfig.resultActions;

  return (
    <>
      <div className="dynamic-platform-view">
        {/* Header */}
        <div className="platform-header">
          <h2>{getTranslation(viewConfig.header.titleKey)}</h2>
          <p>{getTranslation(viewConfig.header.descriptionKey)}</p>
        </div>

        {/* Top actions — v3 uses ActionButton, legacy uses TopActionPanel */}
        {viewConfig.topActions && viewConfig.topActions.length > 0 && (
          <div className="platform-top-actions">
            {isV3
              ? viewConfig.topActions.map(action => (
                  <ActionButton
                    key={action.id}
                    action={action as any}
                    viewState={viewState}
                    isSubmitting={isSubmitting}
                    getTranslation={getTranslation}
                    onExecute={executeAction}
                    platformId={platformView.platformId}
                    context="top"
                  />
                ))
              : viewConfig.topActions.map(action => (
                  <TopActionPanel
                    key={action.id}
                    action={action as any}
                    getTranslation={getTranslation}
                  />
                ))
            }
          </div>
        )}

        {/* Form sections */}
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="platform-sections">
            {viewConfig.sections.map((section) => {
            const visibleSection = {
              ...section,
              components: section.components.filter(component =>
                isComponentVisible(component, formValues)
              ),
            };

            return (
              <SectionRenderer
                key={section.id}
                section={visibleSection}
                formValues={formValues}
                files={files}
                onValueChange={handleValueChange}
                onFileChange={handleFileChange}
                getTranslation={getTranslation}
              />
            );
          })}
          </div>

          {/* v3: fileActions (submit buttons) */}
          {isV3 && viewConfig.fileActions && (
            <div className="platform-submit">
              {executeError && <div className="error-message">{executeError}</div>}
              {viewConfig.fileActions
                .filter(a => evalConditions(a.visibleWhen, viewState))
                .map(action => (
                  <button
                    key={action.id}
                    type="button"
                    className="submit-button"
                    disabled={isSubmitting !== null || !evalConditions(action.enabledWhen, viewState)}
                    onClick={() => executeAction(action).catch(() => {})}
                  >
                    {isSubmitting === action.id
                      ? getTranslation('form.submitting')
                      : getTranslation(action.labelKey)}
                  </button>
                ))}
            </div>
          )}

          {/* Legacy v1/v2: submit buttons from processingModes */}
          {!isV3 && result === null && legacyVisibleSubmitButtons.length > 0 && (
            <div className="platform-submit">
              {executeError && <div className="error-message">{executeError}</div>}
              {legacyVisibleSubmitButtons.map(mode => (
                <button
                  key={mode.id}
                  type="button"
                  className="submit-button"
                  disabled={isSubmitting !== null}
                  onClick={() => handleLegacySubmit(mode)}
                >
                  {isSubmitting !== null && (mode.id === isSubmitting || isSubmitting === '__default__')
                    ? getTranslation('form.submitting')
                    : getTranslation(mode.buttonLabelKey)}
                </button>
              ))}
            </div>
          )}
        </form>

        {/* Result area */}
        {result !== null && (
          <>
            <div className="result-success-banner">
              <span className="result-success-icon">✓</span>
              <div>
                <div className="result-success-title">{getTranslation('result.successTitle')}</div>
                <div className="result-success-meta">{getTranslation('result.rowsExtracted')} {rowCount}</div>
              </div>
            </div>

            <div className="result-table-section">
              <div className="result-table-title">{getTranslation('result.tableTitle')}</div>
              <div className="result-table-wrapper">
                <table className="result-table">
                  <thead>
                    <tr>{headerRow.map((col, i) => <th key={i}>{col}</th>)}</tr>
                  </thead>
                  <tbody>
                    {dataRows.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {row.map((cell, cellIndex) => (
                          <td key={cellIndex}>
                            <input
                              type="text"
                              className="result-cell-input"
                              value={cell}
                              onChange={(e) => handleCellChange(rowIndex + 1, cellIndex, e.target.value)}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="result-table-hint">{getTranslation('result.tableHint')}</div>
            </div>

            {/* v3: resultActions */}
            {isV3 && viewConfig.resultActions && (
              <>
                {/* Regular (non-panel) actions in a flex row */}
                <div className="result-actions">
                  {viewConfig.resultActions
                    .filter(a => evalConditions(a.visibleWhen, viewState) && a.type !== 'panelAction')
                    .map(action => (
                      <ActionButton
                        key={action.id}
                        action={action}
                        viewState={viewState}
                        isSubmitting={isSubmitting}
                        getTranslation={getTranslation}
                        onExecute={executeAction}
                        platformId={platformView.platformId}
                      />
                    ))}
                </div>

                {/* CSV warning — shown after insert to Excel */}
                {insertedToExcel && (
                  <p className="result-csv-warning">{getTranslation('result.saveCsvWarning')}</p>
                )}

                {/* Panel actions on their own full-width row */}
                {viewConfig.resultActions
                  .filter(a => evalConditions(a.visibleWhen, viewState) && a.type === 'panelAction')
                  .map(action => (
                    <div key={action.id} className="result-panel-actions">
                      <ActionButton
                        action={action}
                        viewState={viewState}
                        isSubmitting={isSubmitting}
                        getTranslation={getTranslation}
                        onExecute={executeAction}
                        platformId={platformView.platformId}
                        triggerOnly={true}
                        panelOpen={openResultPanelId === action.id}
                        onPanelOpen={() => setOpenResultPanelId(action.id)}
                        onPanelClose={() => setOpenResultPanelId(null)}
                      />
                    </div>
                  ))}

                {/* Open panel rendered full-width below */}
                {openResultPanelId && (() => {
                  const panelAction = viewConfig.resultActions!.find(a => a.id === openResultPanelId);
                  if (!panelAction || !panelAction.panel) return null;
                  return (
                    <InlinePanel
                      key={openResultPanelId}
                      action={panelAction}
                      viewState={viewState}
                      isSubmitting={isSubmitting}
                      getTranslation={getTranslation}
                      onExecute={executeAction}
                      onClose={() => setOpenResultPanelId(null)}
                    />
                  );
                })()}
              </>
            )}

            {/* Legacy v1/v2: hardcoded result buttons */}
            {!isV3 && (
              <>
                <div className="result-actions">
                  <button className="result-btn result-btn-excel" onClick={handleInsertToExcel}>
                    {getTranslation('result.insertExcel')}
                  </button>
                  <button
                    className="result-btn result-btn-csv"
                    onClick={handleSaveCsv}
                    disabled={!insertedToExcel}
                  >
                    {getTranslation('result.saveCsv')}
                  </button>
                  <button className="result-btn result-btn-new" onClick={handleNewAnalysis}>
                    {getTranslation('result.newAnalysis')}
                  </button>
                </div>
                {insertedToExcel && (
                  <p className="result-csv-warning">{getTranslation('result.saveCsvWarning')}</p>
                )}
                {viewConfig.postProcessing && (
                  <PostProcessing
                    config={viewConfig.postProcessing}
                    spreadsheetDataAvailable={insertedToExcel}
                    platformId={platformView.platformId}
                    tableRows={tableRows}
                    getTranslation={getTranslation}
                    onSubmit={handlePostProcessingSubmit}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Bottom actions — independent of result state */}
      {viewConfig.bottomActions && viewConfig.bottomActions.length > 0 && (
        <>
          <div className="platform-bottom-actions">
            {viewConfig.bottomActions
              .filter(a => evalConditions(a.visibleWhen, viewState))
              .map(action => (
                <ActionButton
                  key={action.id}
                  action={action}
                  viewState={viewState}
                  isSubmitting={isSubmitting}
                  getTranslation={getTranslation}
                  onExecute={executeAction}
                  platformId={platformView.platformId}
                  context="result"
                  triggerOnly={action.type === 'panelAction'}
                  panelOpen={openBottomPanelId === action.id}
                  onPanelOpen={() => setOpenBottomPanelId(action.id)}
                  onPanelClose={() => setOpenBottomPanelId(null)}
                />
              ))}
          </div>

          {/* Open bottom panel rendered full-width below */}
          {openBottomPanelId && (() => {
            const panelAction = viewConfig.bottomActions!.find(a => a.id === openBottomPanelId);
            if (!panelAction || !panelAction.panel) return null;
            return (
              <InlinePanel
                key={openBottomPanelId}
                action={panelAction}
                viewState={viewState}
                isSubmitting={isSubmitting}
                getTranslation={getTranslation}
                onExecute={executeAction}
                onClose={() => setOpenBottomPanelId(null)}
              />
            );
          })()}
        </>
      )}

      {creditWidget && (
        <CreditBalance
          widget={creditWidget}
          credits={creditBalance}
          getTranslation={getTranslation}
        />
      )}
    </>
  );
};

export default DynamicPlatformView;
